import { Box, Text, VStack } from '@chakra-ui/react';
import { TbBuildingStore, TbMapPin, TbPercentage, TbShieldCheck } from 'react-icons/tb';
import {
    PARTBAY_BLUE,
    SettingsRuleStep,
    SettingsRuleSteps,
    SettingsRuleSummary,
    type SettingsRuleStepValue,
} from '../../../../../components/settings/primitives';
import {
    formatLkqPricingStatesLabel,
    LKQ_PRICING_ALL_LABEL,
} from '../../../../../utils/lkqPricingConstants';
import type { LkqPricingRule } from '../../../../../utils/lkqPricingRows';
import {
    draftPercent,
    isAllCompanies,
    isAllStates,
    isAllVendors,
    type PricingRuleDraft,
} from './pricingRuleDraft';
import type { PricingTableSpec } from './pricingTables';

/** How a dimension reads in the sentence when it has not been narrowed. */
const ANY_VENDOR = 'any vendor';
const ANY_COMPANY = 'any insurance company';
const ANY_STATE = 'any state';

/** The rule as one sentence, with the parts still missing left as blanks. */
export const describePricingRuleDraft = (
    draft: PricingRuleDraft,
    spec: PricingTableSpec,
): string => {
    const vendor = isAllVendors(draft) ? ANY_VENDOR : draft.supplierName || 'a vendor';
    const company = isAllCompanies(draft)
        ? ANY_COMPANY
        : draft.insuranceCompanyName || 'an insurance company';
    const states = isAllStates(draft)
        ? ANY_STATE
        : draft.states.length > 0
            ? formatLkqPricingStatesLabel(draft.states)
            : 'a state';
    const percent = draftPercent(draft);
    const outcome = percent === null ? 'the agreed percentage' : `${percent}%`;
    return `If the vendor is ${vendor} and the job is ${company} at a shop in ${states}, ${spec.outcomeLead} ${outcome}.`;
};

export interface PricingRuleStepsProps {
    draft: PricingRuleDraft;
    spec: PricingTableSpec;
    /** Sub companies set to ride along on this rule. */
    sharedCompanyNames: string[];
    /** Sub companies branching onto rules of their own. */
    branchedCompanyNames: string[];
    /** The group mate's rule this one folds into, when merging. */
    mergeTargetRule?: LkqPricingRule;
}

/**
 * The rule the left pane is building, read as steps down a rail: the vendor,
 * the insurance company, the states, and the percentage those three add up to.
 * Same treatment as the custom price match order, because it answers the same
 * question — what did I just build, in the order PartBay reads it.
 *
 * Every step can be left at "All", which is what makes a rule broad rather than
 * unfinished, so a wildcard is shown as a chip of its own instead of as a blank.
 * Shared by the markup, discount and discount flag screens; only the last step
 * and the closing clause of the sentence differ between them.
 */
const PricingRuleSteps = ({
    draft,
    spec,
    sharedCompanyNames,
    branchedCompanyNames,
    mergeTargetRule,
}: PricingRuleStepsProps) => {
    // Folded into a group mate: there is no rule of its own to show, so the
    // pane says what this company will follow instead of drawing empty steps.
    if (draft.joinParentId && mergeTargetRule) {
        return (
            <Box
                borderWidth="1px"
                borderColor={PARTBAY_BLUE}
                borderRadius="lg"
                bg="white"
                px={5}
                py={4}
            >
                <Text fontSize="sm" fontWeight="semibold" color="gray.800" mb={1}>
                    Follows {draft.joinParentName}
                </Text>
                <Text fontSize="sm" color="gray.600">
                    {draft.insuranceCompanyName} is listed under {draft.joinParentName} and takes its{' '}
                    {spec.rowNoun}: {mergeTargetRule.supplierName} at{' '}
                    {formatLkqPricingStatesLabel(mergeTargetRule.states)}, at{' '}
                    {Math.round(mergeTargetRule.percentage * 100)}%. Saving applies it.
                </Text>
            </Box>
        );
    }

    const percent = draftPercent(draft);

    const companyValues: SettingsRuleStepValue[] = draft.insuranceCompanyId
        ? [
              { label: draft.insuranceCompanyName },
              ...sharedCompanyNames.map((name) => ({ label: name, colorScheme: 'blue' })),
              ...branchedCompanyNames.map((name) => ({
                  label: `${name} (own rule)`,
                  colorScheme: 'gray',
              })),
          ]
        : [];

    const stateValues: SettingsRuleStepValue[] = isAllStates(draft)
        ? [{ label: `${LKQ_PRICING_ALL_LABEL} states`, colorScheme: 'purple' }]
        : draft.states.map((state) => ({ label: state, colorScheme: 'purple' }));

    return (
        <VStack align="stretch" spacing={5}>
            <SettingsRuleSteps>
                <SettingsRuleStep
                    index={0}
                    lead="If the vendor is"
                    icon={TbBuildingStore as React.ElementType}
                    values={
                        draft.supplierId
                            ? [
                                  {
                                      label: isAllVendors(draft)
                                          ? `${LKQ_PRICING_ALL_LABEL} vendors`
                                          : draft.supplierName,
                                      colorScheme: 'green',
                                  },
                              ]
                            : []
                    }
                    placeholder="Pick a vendor, or apply this to all of them."
                />
                <SettingsRuleStep
                    index={1}
                    lead="and the job is"
                    icon={TbShieldCheck as React.ElementType}
                    values={
                        isAllCompanies(draft)
                            ? [{ label: `${LKQ_PRICING_ALL_LABEL} insurance companies` }]
                            : companyValues
                    }
                    placeholder="Pick an insurance company, or apply this to all of them."
                />
                <SettingsRuleStep
                    index={2}
                    lead="at a shop in"
                    icon={TbMapPin as React.ElementType}
                    values={stateValues}
                    placeholder="Pick the states this applies in."
                />
                <SettingsRuleStep
                    index={3}
                    lead={spec.outcomeLead}
                    icon={TbPercentage as React.ElementType}
                    values={
                        percent === null ? [] : [{ label: `${percent}%`, colorScheme: 'orange' }]
                    }
                    placeholder={`Enter the ${spec.rowNoun} agreed with this vendor.`}
                />
            </SettingsRuleSteps>

            {/* The same rule as one sentence, which is how it will be explained
                to whoever reads the invoice it lands on. */}
            <SettingsRuleSummary title="Rule summary">
                <Text fontSize="sm" color="gray.700">
                    {describePricingRuleDraft(draft, spec)}
                </Text>
                {branchedCompanyNames.length > 0 && (
                    <Text fontSize="xs" color="gray.500" mt={2}>
                        {branchedCompanyNames.join(', ')}{' '}
                        {branchedCompanyNames.length === 1 ? 'starts' : 'start'} from this rule and
                        can be retuned from{' '}
                        {branchedCompanyNames.length === 1 ? 'its' : 'their'} own row.
                    </Text>
                )}
            </SettingsRuleSummary>
        </VStack>
    );
};

export default PricingRuleSteps;
